export type ConsentState = {
  analytics: boolean
  marketing: boolean
}

type StoredConsent = ConsentState & { v: number }

import { loadGoogleTagManager, shouldLoadGtm } from "./googleTagManager"

export const CONSENT_COOKIE = "stz_consent"
export const OPEN_PREFERENCES_EVENT = "stz:open-cookie-preferences"

const CONSENT_VERSION = 1
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

const TRACKER_COOKIE_PATTERNS = {
  analytics: [/^_ga/, /^_gid$/, /^_gat/],
  marketing: [
    /^_fbp$/,
    /^_fbc$/,
    /^_pin_unauth$/,
    /^_pinterest_/,
    /^_derived_epik$/,
    /^_ttp$/,
    /^_tt_enable_cookie$/,
    /^ttcsid/,
    /^__kla_/,
    /^hubspotutk$/,
    /^__hs/,
    /^_gcl_/,
    /^_uet/,
    /^addshoppers/i,
  ],
}

const MARKETING_LOCAL_STORAGE = [
  /^_gcl_ls$/,
  /^_pin_unauth_ls$/,
  /^_derived_epik_ls$/,
  /^fbapc_/,
  /^__kl_key$/,
  /^klaviyoOnsite$/,
  /^kl-post-identification-sync$/,
  /^kl-/,
  /^_uetvid$/,
  /^_uetsid$/,
  /^asPageViews$/,
  /^asTimeOnSiteValue$/,
  /^asHasViewedProductPage$/,
  /^AS_DATA_PRIVACY_DATA_REGULATED$/,
]

type Listener = (consent: ConsentState) => void
const listeners = new Set<Listener>()

const isBrowser = () => typeof window !== "undefined"

export const hasGlobalPrivacyControl = (): boolean =>
  // @ts-ignore
  isBrowser() && navigator.globalPrivacyControl === true

const readCookie = (name: string): string | null => {
  if (!isBrowser()) return null
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null
}

const readStoredConsent = (): StoredConsent | null => {
  const raw = readCookie(CONSENT_COOKIE)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (parsed?.v !== CONSENT_VERSION) return null
    return {
      v: parsed.v,
      analytics: parsed.analytics !== false,
      marketing: parsed.marketing !== false,
    }
  } catch (e) {
    return null
  }
}

export const hasStoredConsent = (): boolean => readStoredConsent() !== null

export const getConsent = (): ConsentState => {
  const stored = readStoredConsent()
  const consent = stored
    ? { analytics: stored.analytics, marketing: stored.marketing }
    : { analytics: true, marketing: true }
  if (hasGlobalPrivacyControl()) consent.marketing = false
  return consent
}

export const toGoogleConsent = ({ analytics, marketing }: ConsentState) => {
  const ads = marketing ? "granted" : "denied"
  return {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  }
}

const domainVariants = (): (string | undefined)[] => {
  const parts = window.location.hostname.split(".")
  const variants: (string | undefined)[] = [undefined]
  for (let i = 0; i < parts.length - 1; i++) {
    variants.push(`.${parts.slice(i).join(".")}`)
  }
  return variants
}

const deleteCookie = (name: string) => {
  domainVariants().forEach((domain) => {
    document.cookie = `${name}=; Max-Age=0; path=/${
      domain ? `; domain=${domain}` : ""
    }`
  })
}

const clearMarketingLocalStorage = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => MARKETING_LOCAL_STORAGE.some((re) => re.test(key)))
      .forEach((key) => localStorage.removeItem(key))
  } catch (e) {
    /* private mode */
  }
}

const clearTrackerCookies = (consent: ConsentState) => {
  const patterns = [
    ...(consent.analytics ? [] : TRACKER_COOKIE_PATTERNS.analytics),
    ...(consent.marketing ? [] : TRACKER_COOKIE_PATTERNS.marketing),
  ]
  if (patterns.length) {
    document.cookie
      .split("; ")
      .map((row) => row.split("=")[0])
      .filter((name) => patterns.some((pattern) => pattern.test(name)))
      .forEach(deleteCookie)
  }
  if (!consent.marketing) clearMarketingLocalStorage()
}

const pushUetConsent = (consent: ConsentState, mode: "default" | "update") => {
  if (!isBrowser()) return
  // @ts-ignore — Bing UET tags in GTM read this when Consent Mode is wired
  const uetq = window.uetq || (window.uetq = [])
  uetq.push("consent", mode, {
    ad_storage: consent.marketing ? "granted" : "denied",
  })
}

const updateGoogleConsent = (consent: ConsentState) => {
  // @ts-ignore
  if (typeof window.gtag === "function") {
    // @ts-ignore
    window.gtag("consent", "update", toGoogleConsent(consent))
  }
  pushUetConsent(consent, "update")
}

export const setConsent = (next: ConsentState) => {
  if (!isBrowser()) return
  const previous = getConsent()
  const consent = {
    analytics: next.analytics,
    marketing: next.marketing && !hasGlobalPrivacyControl(),
  }
  const value = encodeURIComponent(
    JSON.stringify({ v: CONSENT_VERSION, ...consent })
  )
  const secure = window.location.protocol === "https:" ? "; Secure" : ""
  document.cookie = `${CONSENT_COOKIE}=${value}; Max-Age=${CONSENT_MAX_AGE_SECONDS}; path=/; SameSite=Lax${secure}`

  updateGoogleConsent(consent)
  clearTrackerCookies(consent)
  listeners.forEach((listener) => listener(consent))

  // Pixels that already ran keep tracking until the page is unloaded.
  const revoked =
    (previous.analytics && !consent.analytics) ||
    (previous.marketing && !consent.marketing)
  if (revoked) {
    window.location.reload()
    return
  }
  if (shouldLoadGtm(consent)) loadGoogleTagManager()
}

export const subscribeToConsent = (listener: Listener) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export type PreferencesFocus = "analytics" | "marketing"

export const openCookiePreferences = (focus?: PreferencesFocus) => {
  if (isBrowser()) {
    window.dispatchEvent(
      new CustomEvent(OPEN_PREFERENCES_EVENT, { detail: { focus } })
    )
  }
}

/** Host substrings blocked when Advertising / ad_storage is denied (GTM tags without consent). */
export const AD_NETWORKS_BLOCKED_WHEN_ADS_DENIED = [
  "bat.bing.com",
  "tracead.com",
  "ade.googlesyndication.com",
  "pagead2.googlesyndication.com",
  "googleads.g.doubleclick.net",
]

/**
 * Runs inline in <head> before gtag/GTM load, so it can't import anything.
 */
export const consentDefaultScript = `
(function () {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  var consent = { analytics: true, marketing: true };
  try {
    var match = document.cookie.split("; ").find(function (row) {
      return row.indexOf("${CONSENT_COOKIE}=") === 0;
    });
    if (match) {
      var stored = JSON.parse(decodeURIComponent(match.slice(${
        CONSENT_COOKIE.length + 1
      })));
      if (stored && stored.v === ${CONSENT_VERSION}) {
        consent.analytics = stored.analytics !== false;
        consent.marketing = stored.marketing !== false;
      }
    }
  } catch (e) {}
  if (navigator.globalPrivacyControl === true) consent.marketing = false;
  var ads = consent.marketing ? "granted" : "denied";
  window.gtag("consent", "default", {
    analytics_storage: consent.analytics ? "granted" : "denied",
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500
  });
  window.gtag("set", "ads_data_redaction", true);
  window.uetq = window.uetq || [];
  window.uetq.push("consent", "default", { ad_storage: ads });
  if (ads === "denied") {
    var blocked = ${JSON.stringify(AD_NETWORKS_BLOCKED_WHEN_ADS_DENIED)};
    function blockUrl(url) {
      if (!url) return false;
      var s = String(url);
      for (var i = 0; i < blocked.length; i++) {
        if (s.indexOf(blocked[i]) !== -1) return true;
      }
      return false;
    }
    var append = Node.prototype.appendChild;
    Node.prototype.appendChild = function (child) {
      if (child && blockUrl(child.src)) return child;
      return append.call(this, child);
    };
    var insert = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function (child, ref) {
      if (child && blockUrl(child.src)) return child;
      return insert.call(this, child, ref);
    };
    var setAttr = Element.prototype.setAttribute;
    Element.prototype.setAttribute = function (name, value) {
      if ((name === "src" || name === "href") && blockUrl(value)) return;
      return setAttr.call(this, name, value);
    };
    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function (input) {
        var url = typeof input === "string" ? input : input && input.url;
        if (blockUrl(url)) return Promise.resolve(new Response("", { status: 204 }));
        return origFetch.apply(this, arguments);
      };
    }
    var xhrOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (blockUrl(url)) return;
      return xhrOpen.apply(this, arguments);
    };
    if (navigator.sendBeacon) {
      var origBeacon = navigator.sendBeacon.bind(navigator);
      navigator.sendBeacon = function (url, data) {
        if (blockUrl(url)) return true;
        return origBeacon(url, data);
      };
    }
  }
  if (consent.analytics || consent.marketing) {
    window.dataLayer.push({
      platform: "gatsby",
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });
    var gtm = document.createElement("script");
    gtm.async = true;
    gtm.src = "https://www.googletagmanager.com/gtm.js?id=GTM-ZJ7X7V8";
    document.head.appendChild(gtm);
  }
})();
`
